import { Box, Button, Grid, InputLabel, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import CustomDatePicker from "../../../../Components/DatePicker/DatePicker";
import Input from "../../../../Components/Input/Input";
import Modal from "../../../../Components/Modal/Modal";
import { useStyle } from "../Attendance/attendance-styles";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import AutoCompleteSearch from "../../../../Components/AutoCompleteSearch/AutoCompleteSearch";
import { getAllEmployees } from "../../../../Redux/Actions/HR";
import { ticketsValidation } from "../../../../Utils/Validation";
import { errorNotify } from "../../../../Utils/toast";
import {
  createTicket,
  ticketsDetail,
  updateTicketContent,
} from "../../../../Redux/Actions/Tickets";

const TicketCreateModal = ({
  modalOpen,
  setModalOpen,
  editTicketId = null,
  onEditComplete,
}) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const [last_date, setEndDate] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const { loading: createLoading, success } = useSelector(
    (state) => state.CreateTicket,
  );
  const { loading: updateLoading } = useSelector(
    (state) => state.updateTicketContent,
  );
  const { loading: detailLoading, indivisualTicketDetails } = useSelector(
    (state) => state.ticketDetail,
  );
  const isEditMode = Boolean(editTicketId);
  const loading = isEditMode ? updateLoading : createLoading;

  const { employees: filtered } = useSelector(
    (state) => state.filteredEmployees,
  );

  const excludedEmployeeId = "681365719cef304f9a902935";
  const excludedFilteredEmployees = filtered?.employees?.filter(
    (emp) => emp._id !== excludedEmployeeId,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const resetForm = () => {
    reset();
    setEmployeeId("");
    setEndDate("");
  };

  useEffect(() => {
    dispatch(getAllEmployees());
  }, [dispatch]);

  useEffect(() => {
    if (success && !isEditMode) {
      resetForm();
    }
  }, [success, isEditMode]);

  useEffect(() => {
    if (isEditMode && modalOpen) {
      dispatch(ticketsDetail(editTicketId));
    }
  }, [isEditMode, modalOpen, editTicketId, dispatch]);

  useEffect(() => {
    if (!isEditMode || !indivisualTicketDetails) {
      return;
    }

    setEmployeeId(indivisualTicketDetails.assignTo?._id || "");
    setEndDate(indivisualTicketDetails.last_date || "");
    reset({
      subject: indivisualTicketDetails.subject || "",
      description: indivisualTicketDetails.description || "",
      priority: indivisualTicketDetails.priority || "",
    });
  }, [isEditMode, indivisualTicketDetails, reset]);

  useEffect(() => {
    if (modalOpen && !isEditMode) {
      resetForm();
    }
  }, [modalOpen, isEditMode]);

  const onSubmitHandler = handleSubmit(async (data) => {
    if (!employeeId) {
      errorNotify("Please select employee to assign ticket");
      return;
    }
    if (!last_date) {
      errorNotify("Please enter valid last date");
      return;
    }

    const ticketData = {
      ...data,
      last_date: last_date,
      assignTo: employeeId,
    };

    if (isEditMode) {
      dispatch(updateTicketContent(editTicketId, ticketData));
      return;
    }

    dispatch(createTicket(ticketData));
    resetForm();
  });

  const handleEndDate = (dateValue) => {
    setEndDate(dateValue);
  };

  const closeModal = () => {
    resetForm();
    onEditComplete?.();
    setModalOpen(false);
  };

  const handleModalOpenChange = (open) => {
    if (!open) {
      resetForm();
      onEditComplete?.();
    }
    setModalOpen(open);
  };

  return (
    <Modal
      open={modalOpen}
      setOpen={handleModalOpenChange}
      title={isEditMode ? "Edit Ticket" : "Create New Ticket"}
      size="1000"
    >
      <React.Fragment>
        {isEditMode && detailLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Spinner animation="border" />
          </Box>
        ) : (
          <Form onSubmit={onSubmitHandler}>
            <Box>
              <Grid container columnSpacing={7}>
                <Grid item md={6}>
                  <InputLabel
                    className={classes.department_label}
                    sx={{ mb: 1 }}
                  >
                    Assign To
                  </InputLabel>
                  <AutoCompleteSearch
                    data={excludedFilteredEmployees}
                    departmentData={employeeId}
                    setDepartmentData={setEmployeeId}
                    title={"Search Employee..."}
                  />
                </Grid>
                <Grid item md={6}>
                  <Box>
                    <Input>
                      <Form.Label>Priority</Form.Label>
                      <Form.Select
                        aria-label="Default select example"
                        {...register("priority", ticketsValidation.priority)}
                      >
                        <option value="">Select Priority Level</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </Form.Select>
                    </Input>
                    {errors.priority ? (
                      <small className="text-danger">
                        {errors.priority && errors.priority.message}
                      </small>
                    ) : null}
                  </Box>
                </Grid>
                <Grid item md={6}>
                  <Box sx={{ my: 2 }}>
                    <InputLabel className={classes.department_label}>
                      Ticket Subject
                    </InputLabel>
                    <TextField
                      sx={{ mt: 1 }}
                      placeholder="Enter Ticket Subject"
                      fullWidth
                      className={classes.inputContainer}
                      {...register("subject", ticketsValidation.subject)}
                    />

                    {errors.subject ? (
                      <small className="text-danger">
                        {errors.subject && errors.subject.message}
                      </small>
                    ) : null}
                  </Box>
                </Grid>
                <Grid item md={6}>
                  <Box sx={{ my: 1 }}>
                    <CustomDatePicker
                      label={"Date"}
                      value={last_date}
                      onChangeHandler={handleEndDate}
                    />
                  </Box>
                </Grid>
                <Grid item md={12}>
                  <Box sx={{ my: 1 }}>
                    <InputLabel className={classes.department_label}>
                      Ticket Description
                    </InputLabel>
                    <TextField
                      sx={{ mt: 1 }}
                      placeholder="Enter Ticket Description"
                      fullWidth
                      className={classes.inputContainer}
                      rows={2}
                      multiline
                      {...register(
                        "description",
                        ticketsValidation.description,
                      )}
                    />
                    {errors.description ? (
                      <small className="text-danger">
                        {errors.description && errors.description.message}
                      </small>
                    ) : null}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
              }}
            >
              <Box sx={{ display: "flex", gap: "10px" }}>
                <Button
                  variant="contained"
                  type="button"
                  className={classes.cancelBtn}
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  className={classes.createLeaveBtn}
                >
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </React.Fragment>
    </Modal>
  );
};

export default TicketCreateModal;
